import { gql } from '@memberjunction/graphql-dataprovider';

/**
 * The Research Accelerator / AGU Member Hub GraphQL surface
 * (apps/MJAPI/src/resolvers/ResearchSearchResolver.ts,
 * ResearchMemberResolver.ts — apps/MJAPI/schema.graphql is the wire truth).
 *
 * `gql` here is graphql-request's passthrough tag — it returns a plain string,
 * not a DocumentNode, which is what `GraphQLDataProvider.ExecuteGQL` expects.
 * String interpolation of fragments is fine for the same reason.
 *
 * Every match type carries `itemState { saved pinned dismissed }` (Ruling R19)
 * — the ItemStateFields fragment below is spread into every match fragment so
 * a toggle on any card, list or the Home strip agrees.
 *
 * Fragment composition rule (fixes a live GRAPHQL_VALIDATION_FAILED defect —
 * "There can be only one fragment named ItemStateFields"): a fragment
 * constant below holds ONLY its own `fragment Name on Type { ... }`
 * definition — it never embeds another fragment's definition, only a
 * `...Name` spread. Every operation that needs a fragment (and, transitively,
 * that fragment's own deps) is assembled with `ComposeDocument`, which
 * dedupes fragment definitions by name so a document that composes two or
 * more match fragments — Recommendations, Home, Search-everything — still
 * declares a shared leaf fragment like `ItemStateFields` exactly once.
 */

// ---------------------------------------------------------------------------
// ComposeDocument
// ---------------------------------------------------------------------------

/**
 * Assembles a full GraphQL document from an operation body plus the fragment
 * definitions it needs. Appends each fragment's definition exactly once —
 * deduped by the fragment's own name (`fragment <Name> on`), first-seen order
 * preserved — regardless of how many times it's passed in or by how many
 * different match fragments it's required. Callers pass every fragment a
 * document's selections spread, INCLUDING a match fragment's own transitive
 * deps (e.g. `GRANT_MATCH_FIELDS, ...GRANT_MATCH_DEPS`); ComposeDocument's
 * dedupe is what makes that safe to do for every operation without hand
 * auditing which fragment already pulled in which leaf.
 */
export function ComposeDocument(operation: string, ...fragments: readonly string[]): string {
    const fragmentNamePattern = /fragment\s+(\w+)\s+on/;
    const seenNames = new Set<string>();
    const uniqueFragmentTexts: string[] = [];

    for (const fragmentText of fragments) {
        const nameMatch = fragmentNamePattern.exec(fragmentText);
        const fragmentName = nameMatch ? nameMatch[1] : fragmentText;
        if (seenNames.has(fragmentName)) continue;
        seenNames.add(fragmentName);
        uniqueFragmentTexts.push(fragmentText);
    }

    return [operation, ...uniqueFragmentTexts].join('\n');
}

// ---------------------------------------------------------------------------
// Leaf fragments — each holds ONLY its own definition, no embedded deps
// ---------------------------------------------------------------------------

/** Fragment: a member's saved, pinned and dismissed flags. */
export const ITEM_STATE_FIELDS = gql`
  fragment ItemStateFields on ResearchItemState {
    saved
    pinned
    dismissed
  }
`;

/** Fragment: one overlap (kind and label) between the member and a person. */
export const OVERLAP_FIELDS = gql`
  fragment OverlapFields on ResearchOverlap {
    kind
    label
  }
`;

/** Fragment: a member document's file, extraction status and sizes. */
export const DOCUMENT_FIELDS = gql`
  fragment DocumentFields on ResearchMemberDocument {
    contentType
    documentID
    error
    fileName
    purpose
    sizeBytes
    status
    textLength
    uploadedAt
    consentedAt
  }
`;

// ---------------------------------------------------------------------------
// Match fragments — reference leaf fragments via `...Name`, never embed them
// ---------------------------------------------------------------------------

/** Fragment: every field a grant card shows. */
export const GRANT_MATCH_FIELDS = gql`
  fragment GrantMatchFields on ResearchGrantMatch {
    contentID
    score
    title
    officialTitle
    grantor
    url
    funderType
    closingDate
    closingIsRolling
    postedDate
    awardFloorUSD
    awardCeilingUSD
    awardNotStated
    eligibility
    regions
    description
    isExpired
    isNew
    itemState { ...ItemStateFields }
  }
`;

/** Fragments GRANT_MATCH_FIELDS spreads and does not itself define — pass alongside it to any document that composes it. */
const GRANT_MATCH_DEPS: readonly string[] = [ITEM_STATE_FIELDS];

/** Fragment: every field a person card shows. */
export const PERSON_MATCH_FIELDS = gql`
  fragment PersonMatchFields on ResearchPersonMatch {
    contributorID
    aguPersonID
    aguProfileUrl
    name
    jobTitle
    organization
    location
    orcid
    linkedInUrl
    isMapsMember
    coauthorCount
    lastWorkTitle
    lastWorkYear
    primarySection
    overlaps { ...OverlapFields }
    score
    isNew
    itemState { ...ItemStateFields }
  }
`;

/** Fragments PERSON_MATCH_FIELDS spreads and does not itself define — pass alongside it to any document that composes it. */
const PERSON_MATCH_DEPS: readonly string[] = [OVERLAP_FIELDS, ITEM_STATE_FIELDS];

/**
 * The `ResearchJobMatch` field list, shared verbatim by `JobMatchFields`
 * (search/recommendations/etc., every one of them `[ResearchJobMatch]`) and
 * `JobDetailFields` (`researchJobDetail`, which resolves `ResearchJobDetail`
 * — a DIFFERENT type-graphql object type: `class ResearchJobDetail extends
 * ResearchJobMatch` only copies fields, it does not `implements` an
 * interface, so a fragment typed `on ResearchJobMatch` can never be spread
 * where the schema expects `ResearchJobDetail` — graphql-js rejects it as
 * `objects of type "ResearchJobDetail" can never be of type
 * "ResearchJobMatch"`. One field-list constant, two fragments, keeps the two
 * in lockstep without that trap (fix-round 1 finding #1).
 */
const JOB_MATCH_FIELD_LIST = `
    jobId
    title
    companyName
    companyLogoUrl
    companyType
    city
    state
    country
    remote
    employmentType
    seniority
    gateTier
    gateSection
    salaryMinAnnual
    salaryMaxAnnual
    salaryCurrency
    salaryIsEstimated
    salaryPeriod
    datePosted
    externalUrl
    descriptionSnippet
    otherLocations
    reason
    score
    isNew
    itemState { ...ItemStateFields }
`;

/** Fragment: every field a job card shows. */
export const JOB_MATCH_FIELDS = gql`
  fragment JobMatchFields on ResearchJobMatch {
    ${JOB_MATCH_FIELD_LIST}
  }
`;

/** Fragments JOB_MATCH_FIELDS spreads and does not itself define — pass alongside it to any document that composes it. */
const JOB_MATCH_DEPS: readonly string[] = [ITEM_STATE_FIELDS];

/** `researchJobDetail`'s own fragment — the card fields plus the long-text/apply-link fields a search card never hydrates (see `JOB_MATCH_FIELD_LIST`'s header for why this can't just be `...JobMatchFields`). */
export const JOB_DETAIL_FIELDS = gql`
  fragment JobDetailFields on ResearchJobDetail {
    ${JOB_MATCH_FIELD_LIST}
    description
    companyDescription
    benefits
    applyLinks { site label url }
  }
`;

/** Fragments JOB_DETAIL_FIELDS spreads and does not itself define — pass alongside it to any document that composes it. */
const JOB_DETAIL_DEPS: readonly string[] = [ITEM_STATE_FIELDS];

/** Fragment: every field of a person profile. */
export const PERSON_PROFILE_FIELDS = gql`
  fragment PersonProfileFields on ResearchPersonProfile {
    aguPersonID
    aguProfileUrl
    careerStage
    contributorID
    currentCV { ...DocumentFields }
    department
    email
    funding { endYear funder role startYear title }
    hasVector
    initials
    isSelf
    jobTitle
    linkedInUrl
    location
    memberName
    membershipSince
    name
    orcid
    organization
    peerReview { count venues }
    recentWorks { date kind title url venue }
    researchAreas
    sessionsConvened { meeting title }
    stats { coauthors membersYouConnect worksOnRecord }
  }
`;

/** Fragments PERSON_PROFILE_FIELDS spreads and does not itself define — pass alongside it to any document that composes it. */
const PERSON_PROFILE_DEPS: readonly string[] = [DOCUMENT_FIELDS];

// All three match fragments plus their leaf deps, deduped by ComposeDocument — the set every
// operation that mixes grants + jobs + people (search-all, recommendations, saved items, Home) needs.
const ALL_MATCH_FIELDS: readonly string[] = [
    GRANT_MATCH_FIELDS,
    ...GRANT_MATCH_DEPS,
    JOB_MATCH_FIELDS,
    ...JOB_MATCH_DEPS,
    PERSON_MATCH_FIELDS,
    ...PERSON_MATCH_DEPS,
];

// ---------------------------------------------------------------------------
// Grant Finder
// ---------------------------------------------------------------------------

/** Query: a grant search page. */
export const RESEARCH_GRANT_SEARCH = ComposeDocument(
    gql`
      query ResearchGrantSearch($input: ResearchGrantSearchInput!) {
        researchGrantSearch(input: $input) {
          results { ...GrantMatchFields }
          page
          count
          total
          appliedFilters
          executionTimeMs
          queryMode
          understood {
            searchText
            weight
            cacheHit
            degraded
            alternates
            rerankSkipped
            nonsense
            namedGrant
            judged { kind value }
            filters {
              funderType grantor excludeGrantor postedAfter closingBefore closingAfter includeRolling rollingOnly
              awardMinUSD awardMaxUSD includeUnstatedAward awardUnstatedOnly eligibility region includeClosed fromQuery recommendedOnly
            }
          }
        }
      }
    `,
    GRANT_MATCH_FIELDS,
    ...GRANT_MATCH_DEPS,
);

/** Query: the values the grant filter rail offers. */
export const RESEARCH_GRANT_FACETS = gql`
  query ResearchGrantFacets {
    researchGrantFacets {
      eligibility
      funderTypes
      grantors
      regions
    }
  }
`;

/** Query: one grant for the detail view. */
export const RESEARCH_GRANT_DETAIL = ComposeDocument(
    gql`
      query ResearchGrantDetail($contentID: String!) {
        researchGrantDetail(contentID: $contentID) { ...GrantMatchFields }
      }
    `,
    GRANT_MATCH_FIELDS,
    ...GRANT_MATCH_DEPS,
);

/** Every pinned grant, for the Recommended tab's `Pinned by you` section (P-G9). */
export const RESEARCH_PINNED_GRANTS = ComposeDocument(
    gql`
      query ResearchPinnedGrants {
        researchPinnedGrants { ...GrantMatchFields }
      }
    `,
    GRANT_MATCH_FIELDS,
    ...GRANT_MATCH_DEPS,
);

// ---------------------------------------------------------------------------
// Collaborator Finder
// ---------------------------------------------------------------------------

/** Query: a people search page. */
export const RESEARCH_PEOPLE_SEARCH = ComposeDocument(
    gql`
      query ResearchPeopleSearch($input: ResearchPeopleSearchInput!) {
        researchPeopleSearch(input: $input) {
          results { ...PersonMatchFields }
          page
          count
          total
          appliedFilters
          executionTimeMs
          queryMode
          understood {
            searchText
            alternates
            namedPerson
            cacheHit
            degraded
            rerankSkipped
            nonsense
            constraints {
              kind
              values
              weight
              stance
              origin
              applier
            }
            filters {
              organization
              location
              researchArea
              hasMutualCoauthors
              recommendedOnly
              country
              state
              city
              degreeLevelMin
              yearsExperienceMin
              yearsExperienceMax
              hasFunding
              hasPIFunding
              hasActiveFunding
              mapsMember
            }
          }
        }
      }
    `,
    PERSON_MATCH_FIELDS,
    ...PERSON_MATCH_DEPS,
);

/** Every pinned collaborator, for the Recommended tab's `Pinned by you` section. */
export const RESEARCH_PINNED_PEOPLE = ComposeDocument(
    gql`
      query ResearchPinnedPeople {
        researchPinnedPeople { ...PersonMatchFields }
      }
    `,
    PERSON_MATCH_FIELDS,
    ...PERSON_MATCH_DEPS,
);

/** Query: the values the people filter rail offers. */
export const RESEARCH_PEOPLE_FACETS = gql`
  query ResearchPeopleFacets {
    researchPeopleFacets {
      locations
      organizations
      researchAreas
    }
  }
`;

/** Query: one person's profile. */
export const RESEARCH_PERSON_PROFILE = ComposeDocument(
    gql`
      query ResearchPersonProfile($contributorID: String!) {
        researchPersonProfile(contributorID: $contributorID) { ...PersonProfileFields }
      }
    `,
    PERSON_PROFILE_FIELDS,
    ...PERSON_PROFILE_DEPS,
);

// ---------------------------------------------------------------------------
// Job Finder
// ---------------------------------------------------------------------------

/** Query: a Job Finder search page. */
export const RESEARCH_JOB_PORTAL_SEARCH = ComposeDocument(
    gql`
      query ResearchJobPortalSearch($input: ResearchJobPortalSearchInput!) {
        researchJobPortalSearch(input: $input) {
          available
          results { ...JobMatchFields }
          page
          count
          total
          totalApproximate
          pageableTotal
          queryMode
          appliedFilters
          executionTimeMs
          understood {
            searchText
            cacheHit
            degraded
            alternates
            rerankSkipped
            nonsense
            constraints { kind values weight stance }
            filters { country state city employmentType seniority companySize section industry salaryMinUSD salaryMaxUSD postedAfter countryNot fromQuery recommendedOnly }
          }
        }
      }
    `,
    JOB_MATCH_FIELDS,
    ...JOB_MATCH_DEPS,
);

/** Query: the values the job filter rail offers. */
export const RESEARCH_JOB_FACETS = gql`
  query ResearchJobFacets {
    researchJobFacets {
      countries
      industries
      sections
      states
    }
  }
`;

/** Query: one job for the detail view. */
export const RESEARCH_JOB_DETAIL = ComposeDocument(
    gql`
      query ResearchJobDetail($jobID: String!) {
        researchJobDetail(jobID: $jobID) {
          ...JobDetailFields
        }
      }
    `,
    JOB_DETAIL_FIELDS,
    ...JOB_DETAIL_DEPS,
);

// ---------------------------------------------------------------------------
// Search everything / recommendations / home
// ---------------------------------------------------------------------------

/** Query: the combined search across grants, people and jobs. */
export const RESEARCH_SEARCH_ALL = ComposeDocument(
    gql`
      query ResearchSearchAll($query: String!, $perKind: Int) {
        researchSearchAll(query: $query, perKind: $perKind) {
          executionTimeMs
          grants { ...GrantMatchFields }
          jobs { ...JobMatchFields }
          people { ...PersonMatchFields }
        }
      }
    `,
    ...ALL_MATCH_FIELDS,
);

/** Query: the member's recommendations per kind. */
export const RESEARCH_RECOMMENDATIONS = ComposeDocument(
    gql`
      query ResearchRecommendations($kinds: [String!], $count: Int, $includeCoauthors: Boolean) {
        researchRecommendations(kinds: $kinds, count: $count, includeCoauthors: $includeCoauthors) {
          failedKinds
          grants { ...GrantMatchFields }
          jobs { ...JobMatchFields }
          mode
          newGrants
          newJobs
          newPeople
          people { ...PersonMatchFields }
        }
      }
    `,
    ...ALL_MATCH_FIELDS,
);

const HOME_FIELDS = gql`
  fragment HomeFields on ResearchHome {
    careerTopRole
    displayName
    failedKinds
    hasCV
    hasVector
    lastVisitAt
    newGrants
    newJobs
    newPeople
    picks {
      failedKinds
      grants { ...GrantMatchFields }
      jobs { ...JobMatchFields }
      mode
      newGrants
      newJobs
      newPeople
      people { ...PersonMatchFields }
    }
    previousVisitAt
    recommendedGrants
    recommendedJobs
    recommendedPeople
    researchAreasConfirmed
    savedClosingSoon
    savedClosingSoonDays
    savedClosingSoonTwoWeeks
    visitCount
    visitStreak
    visitsThisMonth
  }
`;

/** Fragments HOME_FIELDS spreads (via `picks`) and does not itself define — pass alongside it to any document that composes it. */
const HOME_FIELDS_DEPS: readonly string[] = ALL_MATCH_FIELDS;

/** Query: the home dashboard's counts and picks. */
export const RESEARCH_HOME = ComposeDocument(
    gql`
      query ResearchHome($timeZone: String) {
        researchHome(timeZone: $timeZone) { ...HomeFields }
      }
    `,
    HOME_FIELDS,
    ...HOME_FIELDS_DEPS,
);

/** Mutation: records a portal visit and returns the refreshed home data. */
export const RESEARCH_RECORD_VISIT = ComposeDocument(
    gql`
      mutation ResearchRecordVisit($timeZone: String) {
        researchRecordVisit(timeZone: $timeZone) { ...HomeFields }
      }
    `,
    HOME_FIELDS,
    ...HOME_FIELDS_DEPS,
);

/** Query: the member's saved items, optionally of one kind. */
export const RESEARCH_SAVED_ITEMS = ComposeDocument(
    gql`
      query ResearchSavedItems($kind: String) {
        researchSavedItems(kind: $kind) {
          grants { ...GrantMatchFields }
          jobs { ...JobMatchFields }
          people { ...PersonMatchFields }
          savedAt { key kind savedAt }
        }
      }
    `,
    ...ALL_MATCH_FIELDS,
);

// ---------------------------------------------------------------------------
// Profile / config / access
// ---------------------------------------------------------------------------

/** Query: the signed-in member's own profile. */
export const RESEARCH_MY_PROFILE = ComposeDocument(
    gql`
      query ResearchMyProfile {
        researchMyProfile { ...PersonProfileFields }
      }
    `,
    PERSON_PROFILE_FIELDS,
    ...PERSON_PROFILE_DEPS,
);

/** Query: the portal's configuration for the surface. */
export const RESEARCH_PORTAL_CONFIG = gql`
  query ResearchPortalConfig {
    researchPortalConfig {
      accountSettingsUrl
      careerMapUrl
      jobsAvailable
      publicProfileUrl
      inTalentNetwork
      adsEnabled
      adPlacements {
        id
        kind
        screens
        house
        logoText
        logoBg
        logoIsAgu
        head
        sub
        cta
        href
        rotate
        personMatch
        mock
      }
      supportEmail
      careerExplorerContactEmail
      careerExplorerPrivacyUrl
      careerExplorerTermsUrl
      cvPollIntervalMs
    }
  }
`;

/** Query: the member's access mode; the one operation that works in denied mode. */
export const RESEARCH_ACCESS = gql`
  query ResearchAccess {
    researchAccess {
      mode
      reason
      displayName
      membershipStatus
      membershipEndsAt
    }
  }
`;

// ---------------------------------------------------------------------------
// Item state
// ---------------------------------------------------------------------------

/** Mutation: sets the saved, pinned or dismissed flag of one item. */
export const RESEARCH_SET_ITEM_STATE = ComposeDocument(
    gql`
      mutation ResearchSetItemState($kind: String!, $key: String!, $saved: Boolean, $pinned: Boolean, $dismissed: Boolean) {
        researchSetItemState(kind: $kind, key: $key, saved: $saved, pinned: $pinned, dismissed: $dismissed) {
          ...ItemStateFields
        }
      }
    `,
    ITEM_STATE_FIELDS,
);

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Mutation: registers a document upload and returns the URL to PUT the file to. */
export const RESEARCH_DOCUMENT_UPLOAD_URL = gql`
  mutation ResearchDocumentUploadUrl($fileName: String!, $contentType: String!, $sizeBytes: Float!, $purpose: String!, $consent: Boolean!) {
    researchDocumentUploadUrl(fileName: $fileName, contentType: $contentType, sizeBytes: $sizeBytes, purpose: $purpose, consent: $consent) {
      documentID
      uploadUrl
    }
  }
`;

/** Mutation: confirms an uploaded document and returns its extraction result. */
export const RESEARCH_DOCUMENT_FINALIZE = ComposeDocument(
    gql`
      mutation ResearchDocumentFinalize($documentID: String!) {
        researchDocumentFinalize(documentID: $documentID) { ...DocumentFields }
      }
    `,
    DOCUMENT_FIELDS,
);

/** Mutation: deletes one of the member's documents. */
export const RESEARCH_DOCUMENT_DELETE = gql`
  mutation ResearchDocumentDelete($documentID: String!) {
    researchDocumentDelete(documentID: $documentID)
  }
`;

/** Query: a short-lived download URL for one of the member's own documents. */
export const RESEARCH_DOCUMENT_DOWNLOAD_URL = gql`
  query ResearchDocumentDownloadUrl($documentID: String!) {
    researchDocumentDownloadUrl(documentID: $documentID)
  }
`;

// ---------------------------------------------------------------------------
// CV and career explorer
// ---------------------------------------------------------------------------

/** Query: the member's current CV with parse state, tags and segments. */
export const RESEARCH_MY_CV = gql`
  query ResearchMyCv {
    researchMyCv {
      documentID
      fileName
      uploadedAt
      source
      detectedType
      parseState
      progress { completed total }
      errorCode
      providerExpiresAt
      consentedAt
      tags { providerTagID name category confidence segmentRef chainRef chainPosition reviewStatus }
      segments { segmentRef text locations { page bbox } }
    }
  }
`;

/** Mutation: approves, declines or resets the review of CV tags. */
export const RESEARCH_CV_REVIEW_TAGS = gql`
  mutation ResearchCvReviewTags($providerTagIDs: [String!]!, $status: String!) {
    researchCvReviewTags(providerTagIDs: $providerTagIDs, status: $status)
  }
`;

/** Mutation (it may call the CV provider): the roles the member's CV matches. */
export const RESEARCH_CAREER_MATCHES = gql`
  mutation ResearchCareerMatches($refresh: Boolean!) {
    researchCareerMatches(refresh: $refresh) {
      schemaVersion
      vocabularyVersion
      scoringVersion
      totalMatches
      fetchedAt
      rereadDroppedDecisions
      providerSupport
      rereadBlocked
      matches {
        rank
        roleRef
        title
        jobFamily
        overlap
        band
        bandLabel
        narrativeKey
        counts { skills education credentials notYetEvidenced blockingCredentials }
        skills { providerTagID name evidenced matchedVia { providerTagID name } cvBacked rationale }
        education { providerTagID name evidenced matchedVia { providerTagID name } cvBacked rationale }
        credentials { providerTagID name evidenced matchedVia { providerTagID name } cvBacked rationale }
        notYetEvidenced { providerTagID name }
        blockingCredentials { providerTagID name }
      }
    }
  }
`;

/** Query: what each role looks like across postings (at most 20 role refs). */
export const RESEARCH_CAREER_JOB_PROFILES = gql`
  query ResearchCareerJobProfiles($roleRefs: [String!]!) {
    researchCareerJobProfiles(roleRefs: $roleRefs) {
      roleRef
      title
      jobFamily
      description
      searchTitles { title postings }
      educationProfile { name share }
      credentialProfile { name share }
      topSkills { name share }
      salary { currency period p25 median p75 postingsWithSalary }
      dataVersion
    }
  }
`;

/** Mutation (it may generate and store the text): why a role fits the member. */
export const RESEARCH_CAREER_NARRATIVE = gql`
  mutation ResearchCareerNarrative($roleRef: String!) {
    researchCareerNarrative(roleRef: $roleRef) {
      roleRef
      narrativeKey
      text
      generated
    }
  }
`;

/** Mutation: the member confirms or withdraws a requirement their CV does not evidence. */
export const RESEARCH_CV_ASSERT_TAG = gql`
  mutation ResearchCvAssertTag($providerTagID: String!, $asserted: Boolean!) {
    researchCvAssertTag(providerTagID: $providerTagID, asserted: $asserted)
  }
`;
